import { UserType } from "@shared/types";
import {
    GroupLocationType,
    ModerationPostUserType,
    PollOption,
    PollOptionResponse,
    POST_TYPES,
    PostResult,
    PreviewCommentUserType,
} from "@infrastructure/Services/PostService.types";
import { CommentImage, CommentType } from "../Comment/CommentTypes";
import { SERVICE_MODALS_OPTIONS_STATES } from "../Modals/ServiceFunctionsModal.types";
import { EntityReactionType } from "@shared/Types/Reactions/ReactionTypes.types";
import { LocationType } from "../SearchHeader/ListItem/ListItemLocation.types";
import React, { ReactNode, RefObject } from "react";
import { GroupTypeEnum } from "../../Pages/GroupDetailsPage/Group.types";
import { POST_SOURCE_TYPE } from "./PostForm/PostFormModal.types";
import { PostCursorableType } from "@infrastructure/Hooks/InfiniteScroll/cursor.types";
import { PollTypesEnum } from "@shared/Types/Polls";

export type PostsCollectionResult = {
    count: number;
    message: string;
    posts: PostResult[];
};

export type PostsCollectionUpdatesResultResponse = PostsCollectionResult & {
    deletedPosts: number[];
};

export type PostsCollectionUpdatesResult = {
    deletedPosts: number[];
    posts: PostType[];
};

export type PostcardDataType = {
    commentsCount: number;
    distanceInKilometers: number | undefined;
    isEditorial: boolean;
    isHighlighted: boolean;
    reactionCountSum: number;
    viewCount: number;
    geoLat?: number;
    geoLong?: number;
    linkoutUrl?: string;
    location?: string;
    onClickNavigateHandler?: () => void;
    postReactions?: EntityReactionType[];
};

export type PostAttributesTypes = {
    groupType: GroupTypeEnum;
    postId: number | string;
    pollsOptions?: PollOption[];
    postData?: PostcardDataType;
    postReactions?: EntityReactionType[];
};

export type PostcardActionButtonsHandlersType = {
    commentButtonText: string;
    isInsidePostDetails: boolean;
    onCommentButtonClick: (
        e: React.MouseEvent<HTMLElement>,
        id: number | string,
    ) => void;
    onReportButtonClick: () => void;
    onShareButtonClicked: () => void;
    showReportOption: boolean;
    user: UserType;
    createReactOnPost?: (
        postId: number,
    ) => (reactionTypeName: string, action: string) => void;
    onLocationButtonClick?: (e: React.MouseEvent<HTMLElement>) => void;
    showCommentButton?: boolean;
    showPollStats?: boolean;
    showShareButton?: boolean;
    pollOptionsHandler: (
        pollOptionId: number,
        type: "create" | "delete",
    ) => void;
};

export type PostcardActionAttributes = PostAttributesTypes &
    PostcardActionButtonsHandlersType;

export type PostIdType = PostType["id"];

export type PostType = {
    cursor: PostCursorableType;
    id: number;
    latestComment?: PreviewComment;
    pageRelated?: {
        boxed?: boolean;
        hideActions?: boolean;
        hideImages?: boolean;
        showCommentButton?: boolean;
        showPreviewComment?: boolean;
        wrapperMarginBottomValue?: string;
    };
    postData: PostDataType;
    status: {
        isEditorial: boolean;
        isHighlighted: boolean;
        isQuestion?: boolean;
    };
    user: UserType;
    group?: PostGroupType;
};

export type ModerationPostType = PostType & {
    askSyncAvailable: boolean;
    isAskSyncInProgress: boolean;
    predictedTags: string[];
    user: ModerationPostUserType;
    exportToAskFinishedAt?: string;
};

export type PostDataType = {
    commentsCount: number;
    content: string;
    createdAt: Date;
    distanceInKilometers: number | undefined;
    isEditorial: boolean;
    isHighlighted: boolean;
    isMediaUnderProcessing: boolean;
    poll: PollType | undefined;
    reactionCountSum: number;
    type: POST_TYPES | undefined;
    updatedAt: Date;
    viewCount: number;
    exportToAskFinishedAt?: string;
    externalSourceType?: string;
    geoLat?: number;
    geoLong?: number;
    images?: PostImage[];
    isReviewed?: boolean;
    linkoutUrl?: string;
    location?: string;
    locationNew?: {
        distanceToReference: number;
        geoLat: number;
        geoLong: number;
        id: string;
        name: string;
        subline: string;
        types: [];
    };
    postReactions?: EntityReactionType[];
    videos?: PostImage[];
};

export type PollType = {
    closedAt: string | undefined;
    pollOptions: PollResponseOption[];
    quizAnswerOptionId: PollResponseOption["id"] | undefined;
    type: PollTypesEnum;
};

export type PollResponseOption = {
    content: string;
    id: number;
    postId: number;
    respondedAt: string | null;
    responseHighlights: PollOptionResponse[];
    responsesCount: number;
    image?: PollOptionImage;
};

type PollOptionImage = {
    id: number;
    url: string;
};

export type PreviewComment = {
    commentReactions: EntityReactionType[];
    content: string;
    createdAt: Date;
    createdBy: PreviewCommentUserType;
    deletedAt: Date | null;
    id: number;
    images: CommentImage[];
    isEditorial: boolean;
    isOwn: boolean;
    postId: number;
    releasedAt: Date | null;
    suspendedAt: Date | null;
    updatedAt: Date | null;
    linkoutUrl?: string;
};

export type PostImage = {
    id: number;
    url: string;
    thumbnailUrl: string;
};

export type PostComponentOptionsType = {
    data: PostType;
    id: number;
    isInsidePostDetails: boolean;
    listType: POST_LIST_TYPES;
    postActions: PostActionsType;
    showGroupName: boolean;
    updateFromServer: (postId: number) => void;
    closeButtonCallback?: () => void;
    scrollableContainerRef?: RefObject<HTMLDivElement> | undefined;
};

export type ModerationPostComponentOptionsType = Omit<
    PostComponentOptionsType,
    "data"
> & {
    data: ModerationPostType;
};

export type PostGroupType = {
    id: number;
    image: string;
    location: GroupLocationType;
    name: string;
    postsCount: number;
    type: string;
    isActive?: boolean;
    parentName?: string;
    groupTypeWithParentName?: string;
    subline: string;
    cityName?: string;
    regionName?: string;
    countryName?: string;
};

export type PostCreationResponse = {
    id: number | string;
    message: string;
};

export type PostActionsType = {
    isInsidePostDetails: boolean;
    onAvatarClick: (user: UserType) => void;
    setIsPostOptionsModalOpen: (isOpen: boolean) => void;
    setModalCurrentAction: (state: SERVICE_MODALS_OPTIONS_STATES) => void;
    setSelectedPost: (
        post: Pick<PostComponentOptionsType, "id" | "data" | "postActions">,
    ) => void;
    autoPlayVideo?: boolean;
    createPollResponseOnPost?: (
        postId: number,
    ) => (pollOptionId: number, action: "create" | "delete") => Promise<void>;
    createReactOnPost?: (
        postId: number,
    ) => (reactionTypeName: string, action: string) => void;
    createReactOnPreviewComment?: (
        commentId: string,
        postId: number,
    ) => (reactionTypeName: string, action: string) => void;
    handleCommentPreviewOptionsClicked?: (comment: CommentType) => void;
    handleCommentPreviewReportClicked?: (comment: CommentType) => void;
    onCardContentClickHandler?: any;
    onCommentButtonClick?: any;
    openPostFormHandler?: (sourceType?: POST_SOURCE_TYPE) => void;
    setHeaderComponent?: (components: ReactNode) => void;
    setInitialPostLocation?: (location: LocationType | undefined) => void;
    showAvatarModal?: any;
    showCommentTeaser?: boolean;
    showOptionsModal?: any;
    updateFromServer?: (postId: number) => void;
};

export enum POST_LIST_TYPES {
    "DASHBOARD_NEWEST_POSTS" = "DASHBOARD_NEWEST_POSTS",
    "POST_DETAILS" = "POST_DETAILS",
    "POST_DETAILS_FULLSCREEN" = "POST_DETAILS_FULLSCREEN",
    "GROUP_PARENT_POSTS" = "GROUP_PARENT_POSTS",
    "POI_DETAILS_POSTS" = "POI_DETAILS_POSTS",
    "PROMO" = "PROMO",
}

export enum POST_CREATION_TARGET {
    GROUP = "group",
    POI = "poi",
}
